export type RouteStepStatus = 'next' | 'upcoming' | 'blocked' | 'arrived';

export type DirectionStep = {
  id: string;
  title: string;
  coachNote: string;
  howTo: string;
  doneSignal: string;
};

export type MemoEntry = {
  id: string;
  time: string;
  type: 'quick' | 'coach' | 'evidence' | 'gym' | 'arrival';
  content: string;
};

export type DonestructionRoute = {
  id: string;
  date: string;
  label: string;
  temporal: 'past' | 'today' | 'future';
  destination: string;
  currentLocation: string;
  nextTurn: string;
  timebox: string;
  owner: string;
  risk: string;
  status: RouteStepStatus;
  instructionPath?: string;
  source?: string;
  definitionOfDone: string;
  detour: string;
  memos?: MemoEntry[];
  steps: DirectionStep[];
  toc: { id: string; title: string }[];
};

export const routes: DonestructionRoute[] = [
  {
    id: '2026-07-02',
    date: '2026-07-02',
    label: 'Previous route',
    temporal: 'past',
    destination: 'Prove that Donestructions can open real instruction pages instead of becoming a checklist.',
    currentLocation: 'The Infisical route proved the open-instructions pattern: a short daily prompt can link to a deeper page with commands, verification, detours, and guardrails.',
    nextTurn: 'Carry forward the pattern, not the infrastructure focus.',
    timebox: '10 min review',
    owner: 'Hermy',
    risk: 'low',
    status: 'arrived',
    instructionPath: '/instructions/setup-infisical-shared-secrets.md',
    source: 'existing prototype + instruction page',
    definitionOfDone: 'The product keeps the openable instruction-page model, but the next route moves to the daily-doc surface where the work actually lives.',
    detour: 'If we need Infisical later, keep it as a systems instruction page — not the first visible product route.',
    steps: [
      {
        id: 'preserve-pattern',
        title: 'Preserve the openable-page pattern',
        coachNote: 'This part was right: the card should open into a page with actual directions.',
        howTo: 'Reuse the instruction page structure: outcome, context, steps, verification, if-stuck, guardrails, arrival criteria.',
        doneSignal: 'Future plays longer than 10 minutes always have an instruction link.'
      }
    ],
    toc: [
      { id: 'destination', title: 'Destination' },
      { id: 'steps', title: 'Route steps' },
      { id: 'done', title: 'Arrival criteria' }
    ]
  },
  {
    id: '2026-07-03',
    date: '2026-07-03',
    label: 'Today',
    temporal: 'today',
    destination: 'Refocus Donestructions around the Outline + Memos daily doc: an openable page with a living capture stream and detailed directions behind each play.',
    currentLocation: 'Katie clarified the priority: “Outline/memos is where it needs to focus first.” The product should now prove the daily doc surface before more systems or dashboard work.',
    nextTurn: 'Stand up the Outline + Memos daily doc path.',
    timebox: '20 min decision · 60–120 min first prototype',
    owner: 'Anthony / Paperclip, Katie review',
    risk: 'medium',
    status: 'next',
    instructionPath: '/instructions/stand-up-outline-memos-daily-doc.md',
    source: 'user-stated direction + donestructions_bundle/02_architecture/outline-memos-hybrid.md',
    definitionOfDone: 'One daily page can show the GPS route, include a Memos timeline section, and open at least one detailed instruction page without turning into a generic task dashboard.',
    detour: 'If Outline or Memos setup blocks the prototype, use a local Markdown/JSON fixture to preserve the product shape and carry forward only the exact auth/API blocker.',
    memos: [
      {
        id: 'memo-001',
        time: 'Now',
        type: 'quick',
        content: 'Katie clarified the first product focus: Outline/Memos before more checklist or infrastructure work.'
      },
      {
        id: 'memo-002',
        time: 'Spec',
        type: 'evidence',
        content: 'Outline = authored collaborative doc. Memos = fast timeline capture. Together they become the daily surface.'
      },
      {
        id: 'memo-003',
        time: 'Coach',
        type: 'coach',
        content: 'Do not build another dashboard first. Prove one page, one timeline, one instruction link.'
      }
    ],
    steps: [
      {
        id: 'choose-target',
        title: 'Choose the first prototype target',
        coachNote: 'This is the next turn. Decide the environment before coding around unknowns.',
        howTo: 'Name the first setup: hosted or local Outline, hosted or local Memos, personal tokens or service account, real API or fixture first.',
        doneSignal: 'The prototype target is written in one sentence and the remaining unknowns are explicit.'
      },
      {
        id: 'shape-doc',
        title: 'Create the daily doc skeleton',
        coachNote: 'The page is the product. Start with the document shape before the integration polish.',
        howTo: 'Use GPS sections: Destination, Current location, Next turn, Route, Katie/Anthony plays, If we only have 20 minutes, Detours, Memos timeline, DoneTally placeholder.',
        doneSignal: 'Opening the page shows the day as directions, not a pile of todos.'
      },
      {
        id: 'timeline-seam',
        title: 'Render the Memos timeline inside the day',
        coachNote: 'Quick capture needs to stay fast; the doc should absorb the stream without making people maintain the doc manually.',
        howTo: 'Start with a simple timeline section fed by Memos API or a local fixture using #quick, #coach, #evidence, #gym, and #arrival tags.',
        doneSignal: 'At least one memo appears in the daily page and can be traced back to its source/id.'
      },
      {
        id: 'connect-instructions',
        title: 'Link one play to a full instruction page',
        coachNote: 'This is what makes Donestructions more than small tasks.',
        howTo: 'Keep the card short and link Open directions → instructions/stand-up-outline-memos-daily-doc.md.',
        doneSignal: 'The route stays scannable, and the details live in the openable instruction page.'
      }
    ],
    toc: [
      { id: 'destination', title: 'Destination' },
      { id: 'current-location', title: 'You are here' },
      { id: 'steps', title: 'Turn-by-turn' },
      { id: 'memos', title: 'Memos timeline' },
      { id: 'detour', title: 'Detour' },
      { id: 'done', title: 'Arrival criteria' }
    ]
  },
  {
    id: '2026-07-06',
    date: '2026-07-06',
    label: 'Next workday',
    temporal: 'future',
    destination: 'Decide whether instruction pages remain Markdown first or become Outline subdocs after the daily doc loop is proven.',
    currentLocation: 'Future decision. Do not answer until one daily doc + timeline has been tested.',
    nextTurn: 'Review what felt better as a doc, memo, or instruction page.',
    timebox: '15 min decision',
    owner: 'Katie + Anthony',
    risk: 'low',
    status: 'upcoming',
    definitionOfDone: 'There is a clear content model for route cards, daily docs, memos, and full instructions.',
    detour: 'If the test has not happened, reroute to completing the daily doc prototype first.',
    steps: [
      {
        id: 'review-content-model',
        title: 'Sort the surfaces by job',
        coachNote: 'Do not collapse everything into one surface. Each layer has a job.',
        howTo: 'Write four bullets: Slack/menu bar = prompt; Outline = daily page; Memos = stream; Instructions = detailed directions.',
        doneSignal: 'Each piece of content has an obvious home.'
      }
    ],
    toc: [
      { id: 'destination', title: 'Destination' },
      { id: 'steps', title: 'Goal setup' },
      { id: 'done', title: 'Arrival criteria' }
    ]
  }
];
